import { Delete } from 'lucide-react';
import { useEffect, useState } from 'react';

type Props = {
  length: 4 | 6;
  disabled?: boolean;
  shake?: boolean;
  onComplete: (pin: string) => void;
};

export function PinKeypad({ length, disabled = false, shake = false, onComplete }: Props) {
  const [pin, setPin] = useState('');

  useEffect(() => {
    if (shake) setPin('');
  }, [shake]);

  useEffect(() => {
    if (pin.length === length) {
      onComplete(pin);
      setPin('');
    }
  }, [pin, length, onComplete]);

  const press = (d: string) => {
    if (disabled || pin.length >= length) return;
    setPin(pin + d);
  };

  const back = () => {
    if (disabled) return;
    setPin(pin.slice(0, -1));
  };

  return (
    <div className="flex flex-col items-center gap-8">
      <div
        className={[
          'flex items-center gap-3',
          shake ? 'animate-[shake_180ms_ease-in-out]' : '',
        ].join(' ')}
      >
        {Array.from({ length }).map((_, i) => {
          const filled = i < pin.length;
          return (
            <span
              key={i}
              className={[
                'inline-block h-3 w-3 rounded-full transition',
                filled ? 'bg-ink' : 'bg-ink/15',
              ].join(' ')}
            />
          );
        })}
      </div>

      <div className="grid grid-cols-3 gap-3">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((d) => (
          <button
            key={d}
            type="button"
            onClick={() => press(String(d))}
            disabled={disabled}
            className="h-[68px] w-[68px] rounded-md bg-surface-muted text-[22px] font-medium text-ink transition active:scale-[0.96] disabled:opacity-40"
          >
            {d}
          </button>
        ))}
        <span aria-hidden />
        <button
          type="button"
          onClick={() => press('0')}
          disabled={disabled}
          className="h-[68px] w-[68px] rounded-md bg-surface-muted text-[22px] font-medium text-ink transition active:scale-[0.96] disabled:opacity-40"
        >
          0
        </button>
        <button
          type="button"
          onClick={back}
          disabled={disabled || pin.length === 0}
          aria-label="O'chirish"
          className="flex h-[68px] w-[68px] items-center justify-center rounded-md text-ink-muted transition active:scale-[0.96] disabled:opacity-40"
        >
          <Delete size={22} />
        </button>
      </div>

      <style>{`@keyframes shake { 0%,100%{transform:translateX(0)} 25%{transform:translateX(-5px)} 75%{transform:translateX(5px)} }`}</style>
    </div>
  );
}
